import { useState, useEffect } from "react";
import moment from "moment";
import { useGetMapTodayCount } from "hooks/console/map/useGetMapTodayCount";
import { useMapSearchStore } from "store/useMapSearchStore";
import { makeIntComma } from "hooks/utils/util";
import SearchInput from "./SearchInput";
import SearchFilterBox from "./SearchFilterBox";
// import LocationSelector from "./LocationSelector";
import { ReactComponent as Logo } from "assets/logo/logo_morning_white.svg";

interface ItemCount {
    name: string;
    count: number;
}

type MapHeaderProps = {
    searchValue: string;
    setSearchValue: React.Dispatch<React.SetStateAction<string>>;
    selectedStatus: string[];
    setSelectedStatus: React.Dispatch<React.SetStateAction<string[]>>;
    search: boolean;
    setSearch: React.Dispatch<React.SetStateAction<boolean>>;
    startDate: Date | null;
    setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
    endDate: Date | null;
    setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
    allStatus: boolean;
    setAllStatus: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function MapHeader({
    searchValue,
    setSearchValue,
    selectedStatus,
    setSelectedStatus,
    search,
    setSearch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    allStatus,
    setAllStatus,
}: MapHeaderProps) {
    const { reFreshFilter, setRefreshFilter } = useMapSearchStore();
    const [refreshTime, setRefreshTime] = useState(moment().format("HH:mm:ss"));
    const [itemCounts, setItemCounts] = useState<ItemCount[]>([]);
    // const [allStatus, setAllStatus] = useState(true);
    // const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
    // const [startDate, setStartDate] = useState<Date | null>(null);
    // const [endDate, setEndDate] = useState<Date | null>(null);
    // const [search, setSearch] = useState(false);
    const { data: todayCountData } = useGetMapTodayCount();

    // 금일 운송장 통계 조회
    const getTodayCounts = async () => {
        const data = todayCountData?.data[0];
        if (data) {
            const updatedCounts = Object.entries(data).map(([key, value]) => ({
                name: key,
                count: Number(value),
            }));
            setItemCounts(updatedCounts);
            setRefreshTime(moment().format("HH:mm:ss"));
        }
    };

    useEffect(() => {
        if (todayCountData) getTodayCounts();
    }, [todayCountData]);

    //상태 전체 체크 true 일때 selectedStatus 초기화
    useEffect(() => {
        if (allStatus) {
            setSelectedStatus([]);
        }
    }, [allStatus]);

    //상태 체크박스 선택시 전체 체크해제
    useEffect(() => {
        if (selectedStatus.length > 0) {
            setAllStatus(false);
        } else {
            setAllStatus(true);
        }
    }, [selectedStatus]);

    //검색 초기화 버튼 클릭시 필터값 초기화
    useEffect(() => {
        if (reFreshFilter) {
            setRefreshFilter(false);
            setSearchValue("");
            setAllStatus(true);
            setStartDate(null);
            setEndDate(null);
        }
    }, [reFreshFilter]);

    //상태 전체 체크박스 선택시
    const handleAllStatusCheck = (checked: boolean) => {
        if (checked) {
            setSelectedStatus([]);
            setAllStatus(checked);
        }
        // else {
        //     setAllStatus(checked);
        // }
    };

    //상태 체크박스 선택시
    const handleStatusCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.currentTarget.checked;
        const value = e.currentTarget.value;
        let updatedList = selectedStatus;

        if (checked) {
            updatedList = updatedList.concat(value);
        } else if (!checked && updatedList.includes(value)) {
            updatedList = updatedList.filter(el => el !== value);
        }

        setSelectedStatus(updatedList);
    };

    //search 값 변경시 초기화
    useEffect(() => {
        if (search) {
            setSearch(false);
        }
    }, [search]);

    return (
        <header>
            <div className="w-full shadow-[0px_0px_12px_0px_rgba(202,214,222,0.60)]">
                <div className="flex">
                    <div className="flex flex-col gap-[16px] w-[320px] bg-bg_logo p-[15px_12px] bg-[linear-gradient(137.22deg,#FE9257_50.75%,#FE8441_50.76%)]">
                        <div className="flex items-center gap-x-[8px]">
                            <Logo />

                            <p className="text-[18px] text-white font-[700]">실시간 배송 조회</p>
                        </div>
                        <SearchInput
                            value={searchValue}
                            handleSubmit={() => {
                                setSearch(true);
                            }}
                            handleChange={e => {
                                setSearchValue(e.currentTarget.value);
                            }}
                        />
                    </div>
                    <div className="flex-1 flex flex-col bg-white">
                        <div className="flex items-center justify-between p-[12px_16px]">
                            <div className="flex items-center gap-x-[20px] flex-1">
                                <div>
                                    <p className="font-[500]">금일 운송장 통계</p>
                                    <div className="flex items-center gap-x-[4px] pt-[4px]">
                                        <span className="text-[#878A93] text-[12px]">{refreshTime}</span>
                                        <button
                                            type="button"
                                            className="font-0 -indent-[9999px] w-[18px] h-[18px] bg-ic_refresh bg-no-repeat bg-center"
                                            onClick={getTodayCounts}
                                        >
                                            새로고침 버튼
                                        </button>
                                    </div>
                                </div>
                                <ul className="flex items-center bg-[#F3F5F8] rounded-[5px]">
                                    {itemCounts.map((item, i) => (
                                        <li key={`item_${i}`} className="flex items-center justify-between p-[10px_12px] min-w-[168px]">
                                            <p className="font-[500] text-[#878A93]">{item.name}</p>
                                            <p className="text-[14px]">
                                                <strong>{makeIntComma(item.count)}</strong> 건
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <SearchFilterBox
                            status={["오더접수", "픽업예약", "픽업완료", "배송중", "배송완료"]}
                            allStatus={allStatus}
                            handleAllStatusCheck={handleAllStatusCheck}
                            handleStatusCheck={handleStatusCheck}
                            selectedStatus={selectedStatus}
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            handleSubmit={() => {
                                setSearch(true);
                            }}
                        />
                    </div>
                </div>
            </div>
        </header>
    );
}
